import React from 'react';
import clsx from 'clsx';
import Icon from '../Icon';

const SEVERITY = {
  critical: { badge: 'badge-danger',  accent: 'var(--err)' },
  high:     { badge: 'badge-warning', accent: 'var(--warn)' },
  medium:   { badge: 'badge-info',    accent: 'var(--info)' },
  low:      { badge: 'badge-muted',   accent: 'var(--tx3)' },
};

const STATUS_BADGE = {
  open:       'badge-danger',
  reviewing:  'badge-warning',
  escalated:  'badge-danger',
  dismissed:  'badge-muted',
  str_filed:  'badge-info',
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const Counter = ({ value, label, accent, alert }) => (
  <div
    style={{
      textAlign: 'center',
      padding: 'var(--s3)',
      background: 'var(--bg-elevated)',
      border: '1px solid',
      borderColor: alert ? accent : 'var(--bd)',
      borderRadius: 'var(--r-md)',
    }}
  >
    <div style={{ fontSize: 24, fontWeight: 800, color: accent, lineHeight: 1.05, letterSpacing: '-0.02em' }}>
      {value}
    </div>
    <div className="text-xs" style={{ color: 'var(--tx3)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
      {label}
    </div>
  </div>
);

const CompliancePanel = ({ summary = {}, flags = [] }) => (
  <div className="card">
    <div className="row row-gap-3 mb-3">
      <Icon name="shield" size={18} style={{ color: 'var(--teal)' }} />
      <div className="card-title" style={{ margin: 0 }}>AML / Compliance</div>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 'var(--s2)', marginBottom: 'var(--s4)' }}>
      <Counter value={summary.open_count || 0}      label="Open"       accent="var(--err)" />
      <Counter value={summary.reviewing_count || 0} label="Reviewing"  accent="var(--warn)" />
      <Counter value={summary.escalated_count || 0} label="Escalated"  accent="var(--err)" />
      <Counter
        value={summary.critical_count || 0}
        label="Critical"
        accent={(summary.critical_count || 0) > 0 ? 'var(--err)' : 'var(--ok)'}
        alert={(summary.critical_count || 0) > 0}
      />
    </div>

    <div className="text-xs mb-2" style={{ color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
      Recent flags
    </div>

    <div style={{ display: 'grid', gap: 'var(--s2)' }}>
      {flags.length === 0 && (
        <div className="empty" style={{ padding: 'var(--s8) var(--s4)' }}>
          <div className="empty-icon"><Icon name="shield" size={22} /></div>
          <h3 style={{ fontSize: 14 }}>No active flags</h3>
          <p>The system is clean. The next scan runs at the top of the hour.</p>
        </div>
      )}
      {flags.map((flag, i) => {
        const sev = SEVERITY[flag.severity] || SEVERITY.low;
        return (
          <div
            key={flag.id || i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--s3)',
              padding: 'var(--s3)',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--bd)',
              borderLeft: `3px solid ${sev.accent}`,
              borderRadius: 'var(--r-md)',
            }}
          >
            <span className={clsx('badge', sev.badge)} style={{ minWidth: 60, textAlign: 'center' }}>
              {flag.severity}
            </span>
            <div className="flex-1" style={{ minWidth: 0 }}>
              <div className="font-semibold" style={{ color: 'var(--tx1)', fontSize: 13 }}>{flag.title}</div>
              <div className="text-xs" style={{ color: 'var(--tx3)', marginTop: 2 }}>
                {flag.flag_type.replace(/_/g, ' ')}
                {flag.corridor ? ` · ${flag.corridor}` : ''}
              </div>
            </div>
            <div className="col" style={{ alignItems: 'flex-end', gap: 4 }}>
              <span className={clsx('badge', STATUS_BADGE[flag.status] || 'badge-muted')}>
                {flag.status}
              </span>
              <span className="text-xs" style={{ color: 'var(--tx3)' }}>{timeAgo(flag.created_at)}</span>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export default CompliancePanel;
